/** "Can the coach advance from this step?" — one predicate per step, which is this repo's
 *  dominant idiom (the dialogs wire an `isStepNValid()` straight to `disabled` on the button).
 *  Derived on every render, never stored: a cached validity flag is one more thing to get stale. */

import { isValidPhoneNumber } from "../../shared/PhoneInput";
import type {
  CheckInsValues,
  GoalsValues,
  IdentityValues,
  NutritionValues,
  WorkoutValues,
} from "./wizardReducer";

export const isIdentityValid = (values: IdentityValues, requiresTrainer: boolean): boolean => {
  if (values.source === "choose") return false;
  if (requiresTrainer && !values.trainerId) return false;

  if (values.source === "existing") return values.existingUser != null;

  if (!values.name.trim() || !values.email.trim()) return false;
  if (!isValidPhoneNumber(values.phoneNumber)) return false;
  if (!values.gender) return false;
  // Length is the server's rule too; the mismatch check is what the old dialog gated on.
  if (values.password.length < 8 || values.password.length > 16) return false;
  return values.password === values.passwordConfirm;
};

/**
 * Goals are all optional, but there has to be at least one of them to be worth a write.
 *
 * `requiresWeekRow` is true only in `configure` mode: there the step edits a week the server
 * already has and must spread it back, so advancing before it loads would post a body with no
 * `weekDate`. In `create` mode no client exists yet — the row is fetched during the commit, right
 * after the trainee is created.
 */
export const isGoalsValid = (values: GoalsValues, requiresWeekRow = true): boolean => {
  if (requiresWeekRow && values.weekRow == null) return false;
  return Boolean(
    values.goal ||
      values.weeklyRange ||
      values.stepsGoal ||
      values.sleepGoalHours ||
      values.caloriesGoal,
  );
};

export const isNutritionValid = (values: NutritionValues): boolean =>
  values.choice === "template" && Boolean(values.templateId);

export const isWorkoutValid = (values: WorkoutValues): boolean => values.templateId != null;

export const isCheckInsValid = (values: CheckInsValues): boolean => {
  const hasSomethingToSend = values.itemTypes.length > 0 || values.formTemplateIds.length > 0;
  if (!hasSomethingToSend) return false;

  // A schedule with no day would never fire, so the server would accept a check-in that silently
  // never arrives. Only relevant when items were actually picked — forms alone are sent directly.
  if (values.mode === "recurring" && values.itemTypes.length > 0) {
    if (values.frequency === "WEEKLY" && values.scheduleDays.length === 0) return false;
    if (values.frequency === "MONTHLY" && values.daysOfMonth.length === 0) return false;
  }
  return true;
};
